<script lang="ts">
	import Textfield from '@smui/textfield/styled';
	import HelperText from '@smui/textfield/helper-text';
	import TextFieldIcon from '@smui/textfield/icon';
	import Dialog, { Title, Content, Actions } from '@smui/dialog/styled';
	import { Link } from 'svelte-routing';
	import { createForm } from 'svelte-forms-lib';
	import * as yup from 'yup';
	import { APP_PATHS } from '../../constants';
	import { interests as interestStore, userInfo, userClientData } from '../../stores';
	import { getClient, mutate, query } from 'svelte-apollo';
	import { allAges, convertAgeGroups } from './newPersonaOptions';
	import SmuiButton, { Label as SmuiLabel, Icon as ButtonIcon } from '@smui/button/styled';
	import AgeSelect from '../../components/Segments/New/AgeSelect.svelte';
	import EstimatedUsers from '../../components/Segments/New/EstimatedUsers.svelte';
	import NewInterests from '../../components/Segments/New/NewInterests.svelte';
	import { graphQLErrors as graphQLErrorsStore } from '../../stores';
	import LookalikeSelect from '../../components/Segments/New/LookalikeSelect.svelte';
	import AppContainer from '../../components/AppContainer.svelte';
	import LoadingOverlay from '../../components/LoadingOverlay.svelte';
	import Card from '../../components/Card.svelte';
	import Text from '../../components/Text.svelte';
	import GenderSelect from '../../components/Segments/GenderSelect.svelte';
	import Range from '../../components/Range.svelte';
	import Button from '../../components/Button.svelte';
	import CardRow from '../../components/CardRow.svelte';
	import GraphQLErrorRender from '../../components/GraphQL/GraphQLErrorRender.svelte';
	import {
		createSegmentsQuery,
		importSegmentToPlatform,
		getLastSegmentQuery
	} from '../../graphql/queries/segments';
	import type { DocumentNode } from 'graphql';
	import { onMount } from 'svelte';
	import type { ApolloQueryResult } from 'apollo-boost';
	import IncomeSelect from '../../components/Segments/New/IncomeSelect.svelte';
	import EducationSelect from '../../components/Segments/New/EducationSelect.svelte';
	import AbcdeSelect from '../../components/Segments/New/AbcdeSelect.svelte';
	import ChatBox from '../../components/Chat/ChatBox.svelte';

	export let isDentsu: boolean;

	const client = getClient();
	const isDevEnv = window.location.hostname === 'dev.app.adpicker.ai';

	const getLastSegmentOp = query(client, {
		query: getLastSegmentQuery
	});

	let confidence = 60;
	let socDemoConfidence = 60;
	let incomeConfidence = 60;
	let educationConfidence = 60;
	let abcdeConfidence = 60;
	let interestsConfidence = 60;
	let trackingpointConfidence = 60;
	let operator = 'AND';
	let interestOperator = 'OR';
	let sendToPlatform = false;
	let errorMessage: string;
	let okDialog = false;
	let errorDialog = false;
	let storedPersona: any[];
	let okPlatformDialog = false;
	let isSaved: boolean;
	let isPlatformSaved: boolean;
	let selected_CZ_SK = 'CZ';
	let segmentIdForPlatform = '';
	let trackingPoint = { value: '', label: '' };
	let dmpSegment = { value: '', label: '' };
	let interestOne = { value: '', label: '' };
	let interestTwo = { value: '', label: '' };
	let dentsuFee = 0.35;
	let defaultFee = '0';
	let lastSegmentId = 0;
	let traitID = '';

	let newDefaultInterests = [];

	if (localStorage.getItem('personaData') === null) {
		storedPersona = ['', [], null, []];
	} else {
		storedPersona = JSON.parse(localStorage.getItem('personaData'));
		localStorage.removeItem('personaData');
	}

	let customName = storedPersona[0];
	let incomeGroups = [];
	let educationGroups = [];
	let abcdeGroups = [];
	let gender = storedPersona[2];
	let ageGroups = storedPersona[3];
	let is_age_group_18_plus = false;
	let is_age_group_18_minus = false;

	const areInterestsSpecified = (
		newDefaultInterests: string[],
		interestOne: { label: string; value: string },
		interestTwo: { label: string; value: string }
	) => newDefaultInterests.length !== 0 || interestOne.value !== '' || interestTwo.value !== '';
	const isIncomeSpecified = (incomeGroups: string[]) => incomeGroups.length === 1;

	function resolvePersonaFee(fee: string) {
		if (isDentsu) {
			return dentsuFee;
		} else {
			return parseFloat(fee);
		}
	}

	function recalculateDefaultFee(
		incomeGroups: string[],
		newDefaultInterests: string[],
		interestOne: { label: string; value: string },
		interestTwo: { label: string; value: string },
		dmpSegment: { label: string; value: string },
		trackingPoint: { label: string; value: string }
	) {
		//lookalike pricing is 0.4
		//default price is 0.35
		//if interests or income are specified, fee is 0.58
		//for SK naming is 0.6
		if (
			(typeof dmpSegment !== 'undefined' && dmpSegment.value !== '') ||
			(typeof trackingPoint !== 'undefined' && trackingPoint.value !== '')
		) {
			dentsuFee = 0.4;
		} else if (selected_CZ_SK === 'CZ') {
			dentsuFee =
				isIncomeSpecified(incomeGroups) ||
				areInterestsSpecified(newDefaultInterests, interestOne, interestTwo)
					? 0.58
					: 0.35;
		} else {
			dentsuFee = 0.6;
		}
	}

	const createSegmentOp = (values: { personaName: string }) => {
		const { personaName } = values;

		const allInterests = [interestOne, interestTwo]
			.filter((v) => {
				return v.value !== '';
			})
			.map((v) => {
				return parseInt(v.value);
			});
		const newOperator = allInterests.length > 1 ? operator : null;
		return new Promise((resolve) =>
			mutate(client, {
				mutation: createSegmentsQuery,
				variables: {
					name: $userClientData.webData.nameFunc(
						personaName,
						$userInfo.client.namePostfix,
						dmpSegment.value,
						trackingPoint.value,
						ageGroups,
						gender,
						newDefaultInterests,
						interestOne,
						interestTwo,
						incomeGroups,
						lastSegmentId
					),
					certainty: confidence / 100,
					sendToPlatform,
					fee: resolvePersonaFee(defaultFee),
					operator: newOperator,
					interests: JSON.stringify(allInterests),
					gender: gender,
					ageGroups: convertAgeGroups(ageGroups),
					is_age_group_18_plus: is_age_group_18_plus,
					is_age_group_18_minus: is_age_group_18_minus,
					socDemoConfidence: socDemoConfidence / 100,
					income: JSON.stringify(incomeGroups),
					certainty_income: incomeConfidence / 100,
					education: JSON.stringify(educationGroups),
					education_certainty: educationConfidence / 100,
					abcde: JSON.stringify(abcdeGroups),
					abcde_certainty: abcdeConfidence / 100,
					default_interests: JSON.stringify(newDefaultInterests.map(Number)),
					certainty_default_interests: interestsConfidence / 100,
					target_id: trackingPoint.value !== '' ? trackingPoint.value : null,
					lookalike_id: dmpSegment.value !== '' ? dmpSegment.value : null,
					lookalike_certainty: trackingpointConfidence / 100,
					interest_operator: interestOperator,
					external_id: traitID
				},
				errorPolicy: 'all'
			})
				.then((dataResponse) => {
					resolve(dataResponse);
					console.log(atob(dataResponse.data.createSegment.segment.id))
					segmentIdForPlatform = atob(dataResponse.data.createSegment.segment.id).replace(
						'Segment:',
						''
					);
				})
				.catch((err) => {
					console.warn(err);
					resolve({ errors: err });
				})
		);
	};

	function handleError(errMsg: string) {
		errorMessage = errMsg;
		errorDialog = true;
		console.error('Error deleting segment', errMsg);
	}

	const { form, errors, state, handleChange, handleSubmit } = createForm({
		initialValues: {
			personaName: customName
		},
		validationSchema: yup.object().shape({
			personaName: yup.string().required().min(3).max(128)
		}),
		onSubmit: async (values) => {
			if (!isDentsu && defaultFee === '' && $userClientData.webData.fee) {
				alert('Please fill default fee');
			} else {
        if (!isDevEnv || confirm("You want to create a segment on DEV environment. Are you sure you want to do that?")) {
          await getLastSegmentOp.refetch();
          getLastSegmentOp.subscribe(async (promise) => {
            await (promise as Promise<ApolloQueryResult<any>>).then((data) => {
              lastSegmentId = data.data.lsegment[0].segmentIntId;
            });
          });
          graphQLErrorsStore.set(undefined);
          return createSegmentOp(values)
            .then(() => {
              isSaved = true;
              okDialog = true;
            })
            .catch((err) => {
              handleError(err.message);
            });
        }
      }
		}
	});

	function handleMessage(event: CustomEvent) {
		newDefaultInterests = [];
		if (event.detail !== null) {
			event.detail.forEach((element: { label: string; value: string }) => {
				newDefaultInterests.push(element.value);
			});
		}
	}

	const toggleSegmentMutation = (mutation: DocumentNode) => {
		return new Promise((resolve) =>
			mutate(client, {
				mutation: mutation,
				variables: {
					seg_id: segmentIdForPlatform
				},
				errorPolicy: 'all'
			})
				.then((responseData) => {
					const { errors } = responseData;
					if (errors) {
						console.log(errors);
						handleError(errors[0].message);
					} else {
						isPlatformSaved = true;
						okPlatformDialog = true;
					}
				})
				.catch((err) => {
					handleError(err.message);
					resolve({ errors: err });
				})
		);
	};

	$: {
		if (trackingPoint.value !== '' || dmpSegment.value !== '') {
			const nonDmpElements = document.querySelectorAll('.non-dmp');
			nonDmpElements.forEach((el) => {
				el.classList.add('hidden-card');
			});
		} else {
			const nonDmpElements = document.querySelectorAll('.non-dmp');
			nonDmpElements.forEach((el) => {
				el.classList.remove('hidden-card');
			});
		}
	}

	$: {
		if (
			gender !== null ||
			ageGroups.length > 0 ||
			newDefaultInterests.length > 0 ||
			incomeGroups.length > 0 ||
			educationGroups.length > 0 ||
			abcdeGroups.length > 0 ||
			interestOne.value !== '' ||
			interestTwo.value !== ''
		) {
			//document.querySelector('.dmp').classList.add('hidden-card');
			trackingPoint = { value: '', label: '' };
			dmpSegment = { value: '', label: '' };
		} else {
			if (document.querySelector('.dmp') !== null) {
				//document.querySelector('.dmp').classList.remove('hidden-card');
			}
		}
	}

	$: {
		if (dmpSegment.value !== '') {
			trackingPoint = { value: '', label: '' };
		}
		if (trackingPoint.value !== '') {
			dmpSegment = { value: '', label: '' };
		}
		if (selected_CZ_SK === 'CZ') {
			recalculateDefaultFee(
				incomeGroups,
				newDefaultInterests,
				interestOne,
				interestTwo,
				dmpSegment,
				trackingPoint
			);
		} else {
			dentsuFee = 0.6;
		}
	}

	const toggleOperator = (event: Event, newOperator: string) => {
		event.preventDefault();
		interestOperator = newOperator;
	};

	$: {
		recalculateDefaultFee(
			incomeGroups,
			newDefaultInterests,
			interestOne,
			interestTwo,
			dmpSegment,
			trackingPoint
		);
	}

	onMount(() => {
		getLastSegmentOp.refetch();
		getLastSegmentOp.subscribe(async (promise) => {
			await (promise as Promise<ApolloQueryResult<any>>).then((data) => {
				lastSegmentId = data.data.lsegment[0].segmentIntId;
			});
		});
	});
</script>

<AppContainer>
	<LoadingOverlay title="Saving..." spinner visible={$state.isSubmitting || $state.isValidating} />

	<form on:submit={handleSubmit}>
		<Card noborder>
			<div class="row">
				<Text>Name:</Text>
				<div class="fullwidth">
					<Textfield
						invalid={$errors['personaName']}
						input$name="personaName"
						class="fullwidth"
						variant="outlined"
						value={$form.personaName}
						on:change={handleChange}
						on:blur={handleChange}
					/>
				</div>
				<div class="text-end">
					<Text>
						{$userClientData.webData.nameEnd}
					</Text>
				</div>
				{#if !$userClientData.webData.default}
					<SmuiButton
						variant="raised"
						type="button"
						on:click={() => {
							$form.personaName = $userClientData.webData.nameFunc(
								$form.personaName,
								$userInfo.client.namePostfix,
								dmpSegment.value,
								trackingPoint.value,
								ageGroups,
								gender,
								newDefaultInterests,
								interestOne,
								interestTwo,
								incomeGroups,
								lastSegmentId,
								true
							);
						}}>Generate name</SmuiButton
					>
				{/if}
			</div>
			<div class="row error">
				<div />
				{#if $errors.personaName}
					<HelperText invalid persistent>{$errors['personaName']}</HelperText>
				{/if}
			</div>
		</Card>

		<Card title="Socio - Demographics" class="non-dmp">
			<div class="row">
				<Text>Gender:</Text>
				<div>
					<GenderSelect bind:gender />
				</div>
			</div>
			<div class="row">
				<Text>Age:</Text>
				<div>
					<AgeSelect
						bind:ageGroups
						bind:is_age_group_18_plus
						bind:is_age_group_18_minus
						options={allAges}
					/>
				</div>
			</div>
			<div class="row">
				<Text>Certainty:</Text>
				<Range bind:value={socDemoConfidence} />
			</div>
		</Card>
		<Card title="Interests" class="non-dmp">
			<div class="row_custom">
				<Text>Interests:</Text>
				<div>
					<div class="interests-select-wrap">
						<NewInterests
							on:message={handleMessage}
							options={$interestStore.filter((interest) => {
								return interest.generalInterest !== null;
							})}
						/>
					</div>
				</div>
			</div>
			<div class="row">
				<Text>Operator:</Text>
				<div>
					<Button
						margin
						variant={interestOperator === 'AND' ? 'raised' : 'outlined'}
						on:click={(e) => toggleOperator(e, 'AND')}
					>
						AND
					</Button>
					<Button
						margin
						variant={interestOperator === 'OR' ? 'raised' : 'outlined'}
						on:click={(e) => toggleOperator(e, 'OR')}
					>
						OR
					</Button>
				</div>
			</div>
			<div class="row">
				<Text>Certainty:</Text>
				<Range bind:value={interestsConfidence} />
			</div>
		</Card>

		<Card title="Income" class="non-dmp">
			<div class="row">
				<Text>Income:</Text>
				<div>
					<div class="interests-select-wrap">
						<IncomeSelect options={['Low', "Mid", 'High']} bind:incomeGroups />
					</div>
				</div>
			</div>
			<div class="row">
				<Text>Certainty:</Text>
				<Range bind:value={incomeConfidence} />
			</div>
		</Card>

		<Card title="Education" class="non-dmp">
			<div class="row">
				<Text>Education:</Text>
				<div>
					<div class="interests-select-wrap">
						<EducationSelect options={["Primary", "Secondary_vocational", "Secondary_with_final_exam", "University"]} bind:educationGroups />
					</div>
				</div>
			</div>
			<div class="row">
				<Text>Certainty:</Text>
				<Range bind:value={educationConfidence} />
			</div>
		</Card>

		<Card title="ABCDE segments" class="non-dmp">
			<div class="row">
				<Text>ABCDE segment:</Text>
				<div>
					<div class="interests-select-wrap">
						<AbcdeSelect options={["A", "B", "C", "D", "E"]} bind:abcdeGroups />
					</div>
				</div>
			</div>
			<div class="row">
				<Text>Certainty:</Text>
				<Range bind:value={abcdeConfidence} />
			</div>
		</Card>

		<!--{#if isUserFromDataSentics}
			<Card title="Custom interests" class="non-dmp">
				<div class="row">
					<Text>Interests:</Text>
					<div>
						<div class="interests-select-wrap">
							<InterestsSelect
								on:change={handleChange}
								bind:interestOne
								bind:interestTwo
								bind:operator
								options={$interestStore.filter((interest) => {
									return interest.generalInterest === null;
								})}
							/>
						</div>
					</div>
				</div>
				<div class="row">
					<Text>Certainty:</Text>
					<Range bind:value={confidence} />
				</div>
			</Card>
		{/if}-->

		<Card title="Lookalikes" class="dmp">
			<div class="row">
				<LookalikeSelect
					on:change={handleChange}
					on:blur={handleChange}
					bind:dmpSegment
				/>
			</div>

			<div class="row infoPopUpBoxParent">
				<div class="info-text-container">
					<Text>Certainty:</Text>
				</div>

				<Range bind:value={trackingpointConfidence} />
			</div>
		</Card>

		<CardRow>
			{#if isDentsu}
				<Card noborder inline>
					<div class="default-fee-card">
						<div class="default-fee-text">Default fee:</div>
						<div class="price-row">
							<div class="default-fee-text">{dentsuFee}</div>
							<TextFieldIcon class="material-icons">euro</TextFieldIcon>
						</div>
					</div>

					<div />
					<div class="row confirmation">
						<div>
							<SmuiButton type="submit" variant="raised" disabled={isSaved}>
								<ButtonIcon class="material-icons">save</ButtonIcon>
								<SmuiLabel>Save</SmuiLabel>
							</SmuiButton>
						</div>
              <div
							title={isSaved && !isPlatformSaved
								? ''
								: 'Start sending the data to ' +
								  $userInfo.client.platformLabel +
								  ' is possible only after saving the segment'}
              >
                <SmuiButton
                  on:click={() => {
                    toggleSegmentMutation(importSegmentToPlatform);
                  }}
                  disabled={!isSaved && !isPlatformSaved}
                  type="button"
                >
                  <SmuiLabel>Start sending data to {$userInfo.client.platformLabel}</SmuiLabel>
                  <ButtonIcon class="material-icons">add</ButtonIcon>
                </SmuiButton>
              </div>
					</div>
				</Card>
			{:else}
				<Card noborder inline>
					{#if $userClientData.webData.fee}
						<div class="row">
							<Text>Default fee</Text>
							<div class={`fullwidth`}>
								<Textfield
									input$name="defaultFee"
									bind:value={defaultFee}
									class="fullwidth"
									variant="outlined"
									withLeadingIcon
									type="number"
									input$step={0.01}
									input$placeholder="0.00"
									input$min={0}
								>
									<TextFieldIcon class="material-icons">euro</TextFieldIcon>
								</Textfield>
							</div>
						</div>
						<div />
					{:else}
						<Text>If you want to replace existing segment, write its ID below:</Text>
						<div class="row">
							<div class={`fullwidth`}>
								<Textfield
									input$name="traitID"
									bind:value={traitID}
									class="fullwidth"
									withLeadingIcon
									type="text"
								/>
							</div>
						</div>
						<div />
					{/if}
					<div class="row confirmation">
						<div>
							<SmuiButton type="submit" variant="raised" disabled={isSaved}>
								<ButtonIcon class="material-icons">save</ButtonIcon>
								<SmuiLabel>Save</SmuiLabel>
							</SmuiButton>
						</div>
              <div
                title={isSaved && !isPlatformSaved
                  ? ''
                  : 'Sending data to ' +
                    $userInfo.client.platformLabel +
                    ' is possible only after saving the segment'}
              >
                <SmuiButton
                  on:click={() => {
                    toggleSegmentMutation(importSegmentToPlatform);
                  }}
                  disabled={!isSaved && !isPlatformSaved}
                  type="button"
                >
                  <SmuiLabel>Start sending data to {$userInfo.client.platformLabel}</SmuiLabel>
                  <ButtonIcon class="material-icons">add</ButtonIcon>
                </SmuiButton>
              </div>
					</div>
				</Card>
			{/if}
			<Card inline flex class="est-card">
				<EstimatedUsers
					{confidence}
					{socDemoConfidence}
					{ageGroups}
					interestOne={interestOne.value}
					interestTwo={interestTwo.value}
					interestsOperator={operator}
					{gender}
					newInterest={newDefaultInterests}
					newConfidence={interestsConfidence}
					income={incomeGroups}
					education={educationGroups}
					abcde={abcdeGroups}
					educationConf={educationConfidence}
					abcdeConf={abcdeConfidence}
					incomeConf={incomeConfidence}
					isLookalike={dmpSegment.value !== '' || trackingPoint.value !== ''}
					lookalikeConf={trackingpointConfidence}
					basicInterestOperator={interestOperator}
				/>
			</Card>
		</CardRow>
		<GraphQLErrorRender />
	</form>
</AppContainer>

<Dialog
	open={okDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">New Segment created</Title>
	<Content id="dialog-content">
		New segment was successfuly created and stored to database.
		<br />
		You can view it in
		<Link to={APP_PATHS.DASHBOARD}>Dashboard</Link>
	</Content>

	<Actions>
		<div style="width: 100%; display: flex; justify-content: space-between;">
      <SmuiButton
				on:click={() => {
					toggleSegmentMutation(importSegmentToPlatform);
				}}
				disabled={!isSaved && !isPlatformSaved}
				type="button"
			>
				<SmuiLabel>Start sending data to {$userInfo.client.platformLabel}</SmuiLabel>
				<ButtonIcon class="material-icons">add</ButtonIcon>
			</SmuiButton>
      <Button on:click={() => {location.reload()}}>Make another</Button>
			<Button on:click={() => (okDialog = false)}>Close</Button>
		</div>
	</Actions>
</Dialog>

<Dialog
	open={errorDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">Error creating new segment</Title>
	<Content id="dialog-content">{errorMessage}</Content>

	<Actions>
		<Button on:click={() => (errorDialog = false)}>Close</Button>
	</Actions>
</Dialog>

<Dialog
	open={okPlatformDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">Import done</Title>
	<Content id="dialog-content">
		The data will be sent to {$userInfo.client.platformLabel} from tomorrow.
		<br />
		You can view your segment in
		<Link to={APP_PATHS.DASHBOARD}>Dashboard</Link>
	</Content>

	<Actions>
		<Button on:click={() => (okPlatformDialog = false)}>Close</Button>
	</Actions>
</Dialog>

{#if $userClientData.webData.name === "demo"}
	<ChatBox />
{/if}

<style>
	.row {
		display: flex;

		/* align-items: flex-start; */
		/* justify-content: flex-start; */
		text-align: left;
		padding: 14px 0;
		flex-direction: column;
	}

	.row_custom {
		display: flex;
		text-align: left;
		padding: 14px 0;
		flex-direction: column;
	}

	.row > :global(div:first-child) {
		padding-bottom: 12px;
	}

	form :global(div.mdc-text-field-helper-text) {
		color: var(--mdc-theme-secondary) !important;
	}

	.interests-select-wrap {
		display: flex;
		/* align-items: center; */
		flex-direction: column;
	}

	.confirmation {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		padding: 50px 0 30px;
	}

	.confirmation div:first-child {
		padding-bottom: 0;
	}

	.confirmation :global(div:first-child button) {
		font-size: 20px;
		height: 56px;
		padding: 0 24px;
	}

	.confirmation :global(div:first-child .mdc-button__icon) {
		font-size: 20px;
		height: 20px;
		margin-right: 18px;
	}

	.name-row {
		display: flex;
		flex: 1;
		flex-direction: row;
		justify-content: space-evenly;
		align-items: center;
		padding-top: 12px;
		padding-bottom: 24px;
		/*padding-left:5px;*/
	}

	.default-fee-card {
		border: 2px solid var(--mdc-theme-primary);
		border-radius: 4px;
		display: flex;
		flex-direction: row;
		justify-content: space-evenly;
		margin-left: -20px;
		margin-right: -20px;
		margin-top: 15px;
		margin-bottom: -5px;
		padding: 20px;
	}

	.price-row {
		display: flex;
		flex-direction: row;
		align-items: center;
	}

	.default-fee-text {
		font-size: 20px !important;
		font-weight: bold;
	}

	@media screen and (min-width: 600px) {
		.row {
			flex-direction: row;
			align-items: center;
		}

		.row > :global(div:first-child) {
			min-width: 110px;
			padding-bottom: 0;
		}

		.interests-select-wrap {
			flex-direction: row;
			align-items: center;
		}

		.row_custom {
			flex-direction: row;
		}

		.row_custom > :global(div:first-child) {
			min-width: 110px;
			padding-top: 10px;
			padding-bottom: 0;
		}
	}

	.row.error {
		padding: 0;
		margin-top: -14px;
	}

	.row.error > :global(div:nth-child(2)) {
		padding-left: 16px;
	}

	:global(.hidden-card) {
		opacity: 0.5;
		pointer-events: none;
		filter: saturate(0%);
	}

	.infoPopUpBoxParent {
		position: relative !important;
	}

	.info-text-container {
		display: flex;
		flex-direction: row;
		align-items: center;
	}

	.text-end {
		margin-left: 20px;
		margin-right: 20px;
	}
</style>
